import { useRef, useState } from "react";
import { UploadCloud, Loader2, X, Link2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function ImageUpload({ value, onChange, testid = "image-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [showUrl, setShowUrl] = useState(false);

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${BACKEND_URL}${data.url}`);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    doUpload(e.dataTransfer.files?.[0]);
  };

  return (
    <div data-testid={testid}>
      {value ? (
        <div className="relative inline-block">
          <img src={value} alt="preview" className="h-32 w-auto max-w-full rounded-sm border border-brand/10 object-cover bg-brand-surface" />
          <button type="button" data-testid={`${testid}-remove`} onClick={() => onChange("")}
            className="absolute -top-2 -right-2 bg-brand-terracotta text-white rounded-full p-1 shadow hover:bg-brand-hover">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div
          onClick={() => !uploading && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          data-testid={`${testid}-dropzone`}
          className={`flex flex-col items-center justify-center gap-2 rounded-sm border-2 border-dashed cursor-pointer py-8 px-4 transition-colors ${dragging ? "border-brand bg-brand-surface" : "border-brand/20 hover:border-brand/40 hover:bg-brand-surface/50"}`}
        >
          {uploading ? (
            <><Loader2 className="w-6 h-6 animate-spin text-brand" /><span className="text-sm text-brand-muted">Uploading…</span></>
          ) : (
            <>
              <UploadCloud className="w-7 h-7 text-brand" strokeWidth={1.5} />
              <span className="text-sm text-brand font-medium">Drag & drop or click to upload</span>
              <span className="text-xs text-brand-muted">JPG, PNG, GIF or WEBP · up to 10MB</span>
            </>
          )}
        </div>
      )}
      <input ref={inputRef} data-testid={`${testid}-input`} type="file" accept="image/*" className="hidden"
        onChange={(e) => { doUpload(e.target.files?.[0]); e.target.value = ""; }} />

      <button type="button" data-testid={`${testid}-url-toggle`} onClick={() => setShowUrl((s) => !s)}
        className="mt-2 inline-flex items-center gap-1 text-xs text-brand-muted hover:text-brand">
        <Link2 className="w-3.5 h-3.5" /> {showUrl ? "Hide URL field" : "Or paste an image URL"}
      </button>
      {showUrl && (
        <input data-testid={`${testid}-url-input`} value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder="https://…" className="mt-1.5 w-full h-10 px-3 rounded-sm border border-brand/20 text-sm outline-none focus:border-brand" />
      )}
    </div>
  );
}
